/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import CampaignModel from 'cio/models/campaign';
import { action } from '@ember/object';
import { task } from 'ember-concurrency';
import { tracked } from '@glimmer/tracking';
import LogModel from 'cio/models/log';
import { inject as service } from '@ember/service';
import ApiErrorsService from 'cio/services/api-errors';
import MomentService from 'cio/services/moment';
import { DataActionComposerSectionType, DataActionTriggerAttributeDropdownItem } from 'cio/utils/campaigns/data-action';
import EventBusService, { LIQUID_ENABLED_FIELD_FOCUS_IN, LIQUID_ENABLED_FIELD_FOCUS_OUT } from 'cio/services/event-bus';
import { typeOf } from '@ember/utils';
import TemplateModel from 'cio/models/template';
import { Editor } from 'cio/services/editor';

interface CreateEventTriggerDataSidebarArgs {
  campaign: CampaignModel;
  template?: TemplateModel;
  pathsUsed: string[];
  onInitialMergeDataLoadFinished: () => unknown;
  selectedEvent: LogModel | null;
  onSelectEvent: (event: LogModel) => unknown;
  onRowClick: (value: string, clickTarget: string | null, clickTargetIndex: number | null, event?: Event) => unknown;
  highlightedItem: DataActionTriggerAttributeDropdownItem | null;
  canClickRows?: boolean;
  events?: LogModel[];
  value?: Record<any, any>;
  theme?: 'dark' | 'light';
  hasReadOnlyPermission: boolean;
  mouseEvent?: 'click' | 'mousedown';
}

export default class CreateEventTriggerDataSidebar extends Component<CreateEventTriggerDataSidebarArgs> {
  @service apiErrors!: ApiErrorsService;
  @service eventBus!: EventBusService;
  @service moment!: MomentService;

  get mouseEvent() {
    return this.args.mouseEvent ?? 'click';
  }

  get theme() {
    return this.args.theme ?? 'dark';
  }

  get events() {
    if (this.args.events != null) {
      return this.args.events;
    }

    return this.webhookEvents;
  }

  get canClickItems() {
    if (this.args.hasReadOnlyPermission) {
      return false;
    }

    if (this.args.canClickRows === false) {
      return false;
    }

    if (!this.args.campaign.isDataCampaign) {
      return this.args.canClickRows ?? false;
    }

    if (this.args.template == null) {
      return false;
    }

    if (this.args.template.editor === Editor.JSON || this.args.template.editor === Editor.JS) {
      return this.targetFieldName === DataActionComposerSectionType.BodyJSON;
    }

    return this.args.template.editor === Editor.DEFAULT;
  }

  get highlightedPath() {
    return this.args.highlightedItem?.value;
  }

  @action
  subscribeToLiquidFocus() {
    this.eventBus.subscribe(LIQUID_ENABLED_FIELD_FOCUS_IN, this, this.liquidFocusIn);
    this.eventBus.subscribe(LIQUID_ENABLED_FIELD_FOCUS_OUT, this, this.liquidFocusOut);
  }

  @action
  unsubscribeFromLiquidFocus() {
    this.eventBus.unsubscribe(LIQUID_ENABLED_FIELD_FOCUS_IN, this, this.liquidFocusIn);
    this.eventBus.unsubscribe(LIQUID_ENABLED_FIELD_FOCUS_OUT, this, this.liquidFocusOut);
  }

  @tracked focusedLiquidField = '';
  @tracked isLiquidInsertionDisabled = true;

  @tracked targetFieldName: DataActionComposerSectionType | null = null;
  @tracked targetFieldIndex: number | null = null;

  private liquidFocusIn(
    targetField:
      | [DataActionComposerSectionType, number]
      | DataActionComposerSectionType.BodyJSON
      | string
      | null
      | undefined,
  ) {
    let targetFieldName: DataActionComposerSectionType | null = null;
    let targetFieldIndex: number | null = null;

    if (typeOf(targetField as any) === 'array') {
      [targetFieldName, targetFieldIndex] = targetField as [DataActionComposerSectionType, number];
    } else if (typeof targetField === 'string') {
      targetFieldName = targetField as any;
    }

    this.targetFieldName = targetFieldName;
    this.targetFieldIndex = targetFieldIndex;
  }

  private liquidFocusOut() {
    this.targetFieldName = null;
    this.targetFieldIndex = null;
  }

  get dropdownEvents() {
    return this.events.map((event, i) => {
      return {
        label: `Sample ${i + 1}`,
        timestamp: this.moment.formatTimestamp([event.timestamp]),
        value: event.id,
        event,
      };
    });
  }

  get isArray() {
    if (this.args.value == null) {
      return false;
    }

    return typeOf(this.args.value) === 'array';
  }

  get value() {
    let value = null;

    if (this.args.value != null) {
      value = this.args.value;
    } else if (this.args.selectedEvent != null) {
      value = this.args.selectedEvent.attrs;
    }

    return value;
  }

  @tracked webhookEvents: LogModel[] = [];

  @action
  loadWebhookCampaignData() {
    if (this.args.campaign.isWebhookTriggered) {
      this.loadWebhookCampaignDataTask.perform();
    }
  }

  loadWebhookCampaignDataTask = task(async () => {
    try {
      this.webhookEvents = await this.args.campaign.fetchWebhookEventLogs();

      if (this.webhookEvents.length > 0) {
        this.args.onSelectEvent(this.webhookEvents[0]);
      }
    } catch (e) {
      this.apiErrors.report(e);
    } finally {
      this.args.onInitialMergeDataLoadFinished();
    }
  });

  @action
  onSelectDropdownItem(item: any) {
    this.args.onSelectEvent(item.event);
  }

  clickTarget: string | null = this.targetFieldName;
  clickTargetIndex = this.targetFieldIndex;

  @action
  onItemContainerMouseDown() {
    // A mousedown event might cause focused inputs/CioAceEditor components to
    // lose focus, which in turn will disable the row click functionality (even
    // as the click is happening).
    // We save the current value of the focused field at the time of the
    // mousedown to be able to fill in the correct input field when the click
    // finished (on mouse up)
    // check whether the row click (when the mouseup happens) can proceed
    this.clickTarget = this.targetFieldName;
    this.clickTargetIndex = this.targetFieldIndex;
  }

  @action
  onItemClick(value: string, event?: Event) {
    this.args.onRowClick(value, this.clickTarget, this.clickTargetIndex, event);
  }
}
